/* Binds Loqate Address Capture to Powerful Form Builder forms. PFB renders asynchronously and
   numbers its fields differently per form, so this waits for the form and maps from label text. */

(function () {
  "use strict";

  const config = window.__loqateAddressCapture;
  if (!config || !config.key) return;

  // Version-pinned. No subresource integrity: SRI on a cross-origin script requires
  // crossorigin="anonymous", and api.addressy.com sends no CORS headers, so adding it blocks
  // the script outright. Self-hosting the SDK is the only route to supply-chain assurance here.
  const SDK_CSS = "https://api.addressy.com/css/address-4.05.min.css";
  const SDK_JS = "https://api.addressy.com/js/address-4.05.min.js";

  const FIELD_PATTERNS = [
    { field: "Line2", test: /address\s*line\s*2/i },
    { field: "Line1", test: /^address(\s*line\s*1)?$/i },
    { field: "City", test: /town|city/i },
    { field: "PostalCode", test: /post\s*?code|zip/i },
    { field: "Province", test: /county|state|province/i }
  ];

  let sdkPromise = null;

  function loadSdk() {
    if (sdkPromise) return sdkPromise;

    sdkPromise = new Promise(function (resolve, reject) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = SDK_CSS;
      document.head.appendChild(link);

      const script = document.createElement("script");
      script.src = SDK_JS;
      // Consent tools auto-block unrecognised third-party scripts, and Capture is functional
      // rather than tracking, so it must be exempt or the lookup silently stops working.
      script.setAttribute("data-cookieconsent", "ignore");
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return sdkPromise;
  }

  function labelTextFor(input) {
    if (!input.id) return "";
    const label = document.querySelector('label[for="' + CSS.escape(input.id) + '"]');
    if (!label) return "";
    return label.textContent.replace(/\*/g, "").replace(/\s+/g, " ").trim();
  }

  // PFB renders its Country select empty, leaving Capture with no search context. Seed it from
  // the visitor's market, and only ever when blank so a customer's own choice survives.
  function applyDefaultCountry(form) {
    const wanted = (config.defaultCountry || "").trim();
    if (!wanted) return;

    let select = null;
    form.querySelectorAll("select").forEach(function (candidate) {
      if (select || !/country/i.test(labelTextFor(candidate))) return;
      select = candidate;
    });
    if (!select || select.value) return;

    const match = [...select.options].find(function (option) {
      return option.value &&
        (option.value.toLowerCase() === wanted.toLowerCase() ||
          option.textContent.trim().toLowerCase() === wanted.toLowerCase());
    });
    if (!match) return;

    select.value = match.value;
    select.dispatchEvent(new Event("change", { bubbles: true }));
  }

  function updateLabelFilled(input) {
    const container = input.closest(".globo-form-control");
    if (!container) return;
    const filled = input.value.trim() !== "";
    container.querySelectorAll(".gfb__label-v2").forEach(function (label) {
      label.classList.toggle("gfb__label__filled", filled);
    });
  }

  function buildFields(form) {
    const fields = [];
    const mapped = [];

    form.querySelectorAll("input[type=text], select").forEach(function (input) {
      const text = labelTextFor(input);
      if (!text) return;

      if (input.tagName === "SELECT") {
        if (/country/i.test(text)) {
          fields.push({ element: input.id, field: "CountryName", mode: window.pca.fieldMode.COUNTRY });
          mapped.push(input);
        }
        return;
      }

      const pattern = FIELD_PATTERNS.find(function (candidate) {
        return candidate.test.test(text);
      });
      if (!pattern) return;

      fields.push({
        element: input.id,
        field: pattern.field,
        mode: pattern.field === "Line1" ? window.pca.fieldMode.DEFAULT : window.pca.fieldMode.POPULATE
      });
      mapped.push(input);
    });

    return { fields: fields, inputs: mapped };
  }

  // Single owner of the button's blocked state, so the address and service rules cannot fight.
  // aria-disabled rather than disabled keeps the button focusable, and the message element stays
  // in the DOM and empties rather than being hidden, which some screen readers fail to announce.
  function createSubmitGate(form) {
    const button = form.querySelector("button[type=submit]");
    if (!button) return null;

    const reasonEl = document.createElement("p");
    reasonEl.className = "loqate-submit-reason";
    reasonEl.id = "loqate-submit-reason-" + Math.random().toString(36).slice(2, 8);
    reasonEl.setAttribute("role", "status");
    button.parentNode.insertBefore(reasonEl, button);

    const reasons = {};

    button.addEventListener("click", function (event) {
      if (button.getAttribute("aria-disabled") !== "true") return;
      event.preventDefault();
      event.stopPropagation();
    });

    return {
      set: function (key, message) {
        if (message) {
          reasons[key] = message;
        } else {
          delete reasons[key];
        }

        const messages = Object.keys(reasons).map(function (name) { return reasons[name]; });
        if (messages.length) {
          button.setAttribute("aria-disabled", "true");
          button.setAttribute("aria-describedby", reasonEl.id);
          reasonEl.textContent = messages.join(" ");
        } else {
          button.removeAttribute("aria-disabled");
          button.removeAttribute("aria-describedby");
          reasonEl.textContent = "";
        }
      },
      isBlocked: function () {
        return button.getAttribute("aria-disabled") === "true";
      }
    };
  }

  function enforceSelection(control, form, mappedInputs, gate) {
    let verified = false;

    function refresh() {
      gate.set("address", verified ? null : config.strings.selectAddress);
    }

    control.listen("populate", function () {
      verified = true;
      refresh();
    });

    // Editing after a selection invalidates it, otherwise a verified address can be typed over.
    mappedInputs.forEach(function (input) {
      input.addEventListener("input", function () {
        if (!verified) return;
        verified = false;
        refresh();
      });
    });

    // An address genuinely absent from the postal file must not trap the customer.
    control.listen("noresults", function () {
      if (form.querySelector(".loqate-manual-entry")) return;

      const escapeHatch = document.createElement("button");
      escapeHatch.type = "button";
      escapeHatch.className = "loqate-manual-entry";
      escapeHatch.textContent = config.strings.addressNotListed;
      escapeHatch.addEventListener("click", function () {
        verified = true;
        refresh();
        escapeHatch.remove();
      });
      form.querySelector("button[type=submit]").parentNode.appendChild(escapeHatch);
    });

    refresh();
    return refresh;
  }

  function enforceBlockedServices(form, gate) {
    const blocked = config.blockedServices || [];
    if (!blocked.length) return null;

    // Blocked values may live in more than one radio group, so group by name rather than
    // assuming the first match speaks for all of them.
    const names = [];
    form.querySelectorAll("input[type=radio]").forEach(function (radio) {
      if (blocked.indexOf(radio.value) === -1 || names.indexOf(radio.name) !== -1) return;
      names.push(radio.name);
    });
    if (!names.length) return null;

    function refresh() {
      const hit = names.some(function (name) {
        const selected = form.querySelector('input[name="' + CSS.escape(name) + '"]:checked');
        return !!selected && blocked.indexOf(selected.value) !== -1;
      });
      gate.set("service", hit ? config.strings.serviceUnavailable : null);
    }

    names.forEach(function (name) {
      form.querySelectorAll('input[name="' + CSS.escape(name) + '"]').forEach(function (radio) {
        radio.addEventListener("change", refresh);
      });
    });

    refresh();
    return refresh;
  }

  function bind(form) {
    if (form.dataset.loqateBound) return;
    form.dataset.loqateBound = "true";

    loadSdk()
      .then(function () {
        applyDefaultCountry(form);

        const mapping = buildFields(form);
        if (!mapping.fields.length) return;

        const control = new window.pca.Address(mapping.fields, {
          key: config.key,
          setCountryByIP: false,
          manualEntryItem: false
        });

        const gate = createSubmitGate(form);
        if (!gate) return;

        control.listen("populate", function () {
          mapping.inputs.forEach(updateLabelFilled);
        });
        mapping.inputs.forEach(function (input) {
          const eventName = input.tagName === "SELECT" ? "change" : "input";
          input.addEventListener(eventName, function () { updateLabelFilled(input); });
          updateLabelFilled(input);
        });

        const refreshers = [enforceBlockedServices(form, gate)];
        if (config.requireSelection) {
          refreshers.push(enforceSelection(control, form, mapping.inputs, gate));
        }

        // PFB restores a localStorage draft, and browser autofill fills fields, without firing
        // change or input events. Re-reading every rule at submit time keeps the gate correct.
        form.addEventListener("submit", function (event) {
          refreshers.forEach(function (refresh) { if (refresh) refresh(); });
          if (!gate.isBlocked()) return;
          event.preventDefault();
          event.stopPropagation();
        }, true);
      })
      .catch(function () {
        // A blocked or failed SDK leaves the form exactly as it was rather than gated shut,
        // but the failure must be visible: address validation is the point of this feature.
        form.dataset.loqateFailed = "true";
        if (window.console && window.console.warn) {
          window.console.warn("[loqate] Address Capture failed to load; forms are submitting without address validation.");
        }
      });
  }

  function watch(container) {
    const existing = container.querySelector("form.g-container");
    if (existing) {
      bind(existing);
      return;
    }

    const observer = new MutationObserver(function () {
      const form = container.querySelector("form.g-container");
      if (!form) return;
      observer.disconnect();
      bind(form);
    });

    observer.observe(container, { childList: true, subtree: true });
  }

  document.querySelectorAll(".globo-formbuilder[data-id]").forEach(watch);
})();
